import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  Input,
  Output,
  Signal,
  signal,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { AuthorizationService } from '@app/shared/services/authorization.service';
import {
  FeatureFlagKey,
  FeatureFlagService,
} from '@app/shared/services/feature-flag.service';
import { ReportIconComponent } from '@assets/icons/report-icon.component';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { Icon, Image } from 'src/assets';
import {
  ArrowRightIconComponent,
  AssetIconComponent,
  AuditLogIconComponent,
  ChatIconComponent,
  HomeIconComponent,
  SectorIconComponent,
  SettingIconComponent,
  ShiftIconComponent,
  TaskIconComponent,
  UserIconComponent,
  VesselIconComponent,
} from 'src/assets/icons';

interface MenuSection {
  label: string;
  routerLink: string;
  icon: any;
  child?: MenuSection[];
  hidden?: Signal<boolean>;
}

@Component({
  standalone: true,
  selector: 'app-sidebar',
  imports: [
    CommonModule,
    RouterLink,
    // Zorro Modules
    NzIconModule,
    NzMenuModule,
    NzLayoutModule,
    ArrowRightIconComponent,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
  @Input() isCollapsed = false;
  @Output() isCollapsedChange = new EventEmitter<boolean>();

  Image = Image;
  Icon = Icon;
  homei = HomeIconComponent;

  activeSubmenu = signal<MenuSection | null>(null);

  menus = signal<MenuSection[]>([
    {
      label: 'Tasks',
      routerLink: '',
      icon: TaskIconComponent,
      hidden: this._isHidden('tasks'),
      child: [
        {
          label: 'Tasks',
          routerLink: '/dashboard/tasks',
          hidden: this._isHidden('tasks'),
          icon: null,
        },
        {
          label: 'Data Sources',
          routerLink: '/dashboard/data-source',
          hidden: this._isHidden('data-source'),
          icon: null,
        },
        {
          label: 'Alerts & Notifications',
          routerLink: '/dashboard/alerts-and-notifications',
          hidden: this._isHidden('alerts-and-notifications'),
          icon: null,
        },
      ],
    },
    {
      label: 'Sectors',
      routerLink: '/dashboard/sectors',
      hidden: this._isHidden('sectors'),
      icon: SectorIconComponent,
    },
    {
      label: 'Chat',
      routerLink: '/dashboard/chat-settings',
      hidden: this._isHidden('chat-settings'),
      icon: ChatIconComponent,
    },
    {
      label: 'Schedule',
      routerLink: '/dashboard/schedule',
      hidden: this._isHidden('schedule'),
      icon: ShiftIconComponent,
    },
    {
      label: 'Users',
      routerLink: '/dashboard/users',
      hidden: this._isHidden('users'),
      icon: UserIconComponent,
    },
    {
      label: 'Vessel Activity',
      routerLink: '/dashboard/vessel-activity',
      hidden: this._isHidden('vessel-activity'),
      icon: VesselIconComponent,
    },
    {
      label: 'Assets',
      routerLink: '/dashboard/assets',
      hidden: this._isHidden('assets'),
      icon: AssetIconComponent,
    },
    {
      label: 'Reports',
      routerLink: '/dashboard/reports',
      hidden: this._isHidden('reports'),
      icon: ReportIconComponent,
    },
    {
      label: 'Audit Log',
      routerLink: '',
      icon: AuditLogIconComponent,
      hidden: this._isHidden('audit-log'),
      child: [
        {
          label: 'Configuration Audit Log',
          routerLink: '/dashboard/configuration-audit-log',
          hidden: this._isHidden('configuration-audit-log'),
          icon: null,
        },
        {
          label: 'User Audit Log',
          routerLink: '/dashboard/user-audit-log',
          hidden: this._isHidden('user-audit-log'),
          icon: null,
        },
      ],
    },
    {
      label: 'Configuration',
      routerLink: '',
      icon: SettingIconComponent,
      hidden: this._isEveryConfigItemHidden([
        ['handover-takeover', 'hoto-task'],
        ['vessel-activity-configuration', 'vessel-activity'],
        ['shift-resource', 'shift-resource'],
      ]),
      child: [
        {
          label: 'Handover/Takeover',
          routerLink: '/dashboard/handover-takeover',
          hidden: this._isConfigItemHidden('handover-takeover', 'hoto-task'),
          icon: null,
        },
        {
          label: 'Vessel Activity',
          routerLink: '/dashboard/vessel-activity-configuration',
          hidden: this._isConfigItemHidden(
            'vessel-activity-configuration',
            'vessel-activity'
          ),
          icon: null,
        },
        {
          label: 'Shift Resource',
          routerLink: '/dashboard/shift-resource',
          hidden: this._isConfigItemHidden('shift-resource', 'shift-resource'),
          icon: null,
        },
      ],
    },
  ]);

  private _hideSubmenuTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private _router: Router,
    private _authorizationService: AuthorizationService,
    private _featureFlagService: FeatureFlagService
  ) {
    this._featureFlagService.loadFeatureFlags();
  }

  hasChildren(item: MenuSection): boolean {
    return !!item.child && item.child.length > 0;
  }

  //active parent menu item
  isParentActive(item: MenuSection): boolean {
    const isHovering = this.activeSubmenu() === item;
    const hasActiveChild = this.isChildRouteActive(item);
    return isHovering || hasActiveChild;
  }

  isChildRouteActive(item: MenuSection): boolean {
    if (!item.child) return false;
    const currentUrl = this._router.url;
    return item.child.some(child => {
      if (!child.routerLink) return false;
      return currentUrl.includes(child.routerLink);
    });
  }

  //show submenu
  showSubmenu(item: MenuSection): void {
    this._clearHideTimeout();

    if (this.hasChildren(item)) {
      this.activeSubmenu.set(item);
    }
  }

  hideSubmenu(): void {
    // Add delay before hiding to allow mouse to move to submenu
    this._hideSubmenuTimeout = setTimeout(() => {
      this.activeSubmenu.set(null);
    }, 100);
  }

  cancelHideSubmenu(): void {
    this._clearHideTimeout();
  }

  private _clearHideTimeout(): void {
    if (this._hideSubmenuTimeout) {
      clearTimeout(this._hideSubmenuTimeout);
      this._hideSubmenuTimeout = null;
    }
  }

  private _isHidden(path: string): Signal<boolean> {
    return computed(() => !this._authorizationService.isAuthorization(path));
  }

  //hide the Configuration parent only when every item under it is hidden
  private _isEveryConfigItemHidden(
    items: [string, FeatureFlagKey][]
  ): Signal<boolean> {
    const hidden = items.map(([path, featureFlagKey]) =>
      this._isConfigItemHidden(path, featureFlagKey)
    );
    return computed(() => hidden.every(isHidden => isHidden()));
  }

  //hide configuration menu item when not authorized OR its feature flag is off
  private _isConfigItemHidden(
    path: string,
    featureFlagKey: FeatureFlagKey
  ): Signal<boolean> {
    return computed(
      () =>
        !this._authorizationService.isAuthorization(path) ||
        !this._featureFlagService.isFeatureEnabled(featureFlagKey)
    );
  }
}
